import { useState, useEffect } from 'react';
import { Clock, WarningCircle, CheckCircle } from '@phosphor-icons/react';

/**
 * Hook to calculate real-time remaining 24-hour customer window for WhatsApp Cloud API (WABA).
 */
export function useWhatsApp24hTimer(lastIncomingTime) {
  const [timeLeft, setTimeLeft] = useState(() => calculateRemaining(lastIncomingTime));

  useEffect(() => {
    setTimeLeft(calculateRemaining(lastIncomingTime));
    const interval = setInterval(() => {
      setTimeLeft(calculateRemaining(lastIncomingTime));
    }, 1000);
    return () => clearInterval(interval);
  }, [lastIncomingTime]);

  return timeLeft;
}

function calculateRemaining(lastIncomingIso) {
  if (!lastIncomingIso) {
    return {
      isActive: false,
      isExpired: true,
      seconds: 0,
      hours: 0,
      minutes: 0,
      secs: 0,
      formatted: 'Expired',
      percent: 0,
    };
  }

  try {
    const incDate = new Date(lastIncomingIso);
    if (isNaN(incDate.getTime())) {
      return { isActive: false, isExpired: true, seconds: 0, hours: 0, minutes: 0, secs: 0, formatted: 'Expired', percent: 0 };
    }
    const expiresDate = new Date(incDate.getTime() + 24 * 60 * 60 * 1000);
    const now = new Date();
    const diffMs = expiresDate.getTime() - now.getTime();

    if (diffMs <= 0) {
      return {
        isActive: false,
        isExpired: true,
        seconds: 0,
        hours: 0,
        minutes: 0,
        secs: 0,
        formatted: 'Expired',
        percent: 0,
      };
    }

    const totalSeconds = Math.floor(diffMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    const percent = Math.min(100, Math.max(0, (diffMs / (24 * 60 * 60 * 1000)) * 100));

    let formatted = '';
    if (hours > 0) {
      formatted = `${hours}h ${minutes}m left`;
    } else if (minutes > 0) {
      formatted = `${minutes}m ${secs}s left`;
    } else {
      formatted = `${secs}s left`;
    }

    return {
      isActive: true,
      isExpired: false,
      seconds: totalSeconds,
      hours,
      minutes,
      secs,
      formatted,
      percent,
    };
  } catch {
    return { isActive: false, isExpired: true, seconds: 0, hours: 0, minutes: 0, secs: 0, formatted: 'Expired', percent: 0 };
  }
}

/**
 * Header live timer badge showing 24h window countdown or expired state.
 */
export function WhatsApp24hHeaderBadge({ lastIncomingTime, className = '' }) {
  const { isActive, formatted } = useWhatsApp24hTimer(lastIncomingTime);

  if (isActive) {
    return (
      <div
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/25 shadow-2xs select-none ${className}`}
        title="WhatsApp 24-Hour Customer Care Window is ACTIVE. Free-form messages can be sent."
        data-testid="wa-24h-active-badge"
      >
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
        </span>
        <Clock size={13} weight="bold" />
        <span className="font-mono font-semibold">{formatted}</span>
      </div>
    );
  }

  return (
    <div
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/25 shadow-2xs select-none ${className}`}
      title="WhatsApp 24-Hour Customer Window EXPIRED. Free-form messages will fail until customer replies or an approved template is sent."
      data-testid="wa-24h-expired-badge"
    >
      <span className="h-2 w-2 rounded-full bg-rose-500"></span>
      <WarningCircle size={13} weight="bold" />
      <span className="font-semibold">24h Window Expired</span>
    </div>
  );
}

/**
 * Compact tag for contact list items in sidebar.
 */
export function WhatsApp24hListBadge({ lastIncomingTime, className = '' }) {
  const { isActive, hours, minutes } = useWhatsApp24hTimer(lastIncomingTime);

  if (isActive) {
    const text = hours > 0 ? `${hours}h left` : `${minutes}m left`;
    return (
      <span
        className={`inline-flex items-center gap-0.5 px-1.5 py-0.2 rounded text-[9px] font-mono font-medium bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 ${className}`}
        title={`24h Window active: ${text}`}
      >
        ⏳ {text}
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-0.5 px-1.5 py-0.2 rounded text-[9px] font-medium bg-muted text-muted-foreground border border-border/40 ${className}`}
      title="24h Customer Window has expired"
    >
      ⌛ Expired
    </span>
  );
}

/**
 * Composer alert banner displayed above the input box when the 24h window has expired.
 */
export function WhatsApp24hExpiredAlert({ isExpired, className = '' }) {
  if (!isExpired) return null;

  return (
    <div
      className={`p-2.5 px-3 bg-amber-500/10 border-t border-b border-amber-500/30 text-amber-800 dark:text-amber-300 text-xs flex items-start gap-2 shadow-2xs ${className}`}
      data-testid="wa-24h-composer-alert"
    >
      <WarningCircle size={16} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
      <div className="flex-1 leading-snug">
        <span className="font-semibold">24-Hour Customer Window Closed: </span>
        More than 24 hours have elapsed since this contact&apos;s last incoming message. Under WhatsApp Cloud API (WABA) regulations, regular outgoing messages cannot be delivered. Send a Meta-approved template or wait for the contact to reply.
      </div>
    </div>
  );
}
